/**
 * Custom hook for phone verification logic
 *
 * This hook encapsulates all the phone verification logic including:
 * - Sending verification codes
 * - Verifying codes
 * - Timer management
 * - Error handling
 *
 * This ensures consistent behavior across all components that need phone verification.
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback when verification succeeds
 * @param {Function} options.onError - Callback when verification fails
 * @param {boolean} options.autoStartTimer - Whether to automatically start timer after sending code (default: true)
 *
 * @returns {Object} Phone verification state and handlers
 *
 * @example
 * const {
 *   phone,
 *   code,
 *   loading,
 *   error,
 *   timer,
 *   codeSent,
 *   setPhone,
 *   setCode,
 *   handleSendCode,
 *   handleVerifyCode,
 *   clearCode
 * } = usePhoneVerification({
 *   onSuccess: () => navigate('/home'),
 *   onError: (err) => console.error(err)
 * });
 */
import { useState, useEffect, useRef } from "react";
import { userAuthAPI } from "../api/userAuthAPI";
import { SESSION_ID } from "../api/apiConfig";
import useUserStore from "../zustand/UseUserStore";
import { toast } from "react-toastify";

export const usePhoneVerification = ({
	onSuccess = () => {},
	onError = () => {},
	autoStartTimer = true,
	creatureID = null,
} = {}) => {
	// Phone verification state
	const [phone, setPhone] = useState("");
	const [code, setCode] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [timer, setTimer] = useState(null);
	const [codeSent, setCodeSent] = useState(false);
	const [codeInputKey, setCodeInputKey] = useState(0); // Key to force remount of code input

	const userStore = useUserStore();
	const cleanedPhone = useRef("");

	// Handles timer countdown
	useEffect(() => {
		if (timer !== null && timer > 0) {
			const timeoutId = setTimeout(() => {
				setTimer((prevTimer) => prevTimer - 1);
			}, 1000);
			return () => clearTimeout(timeoutId);
		}
	}, [timer]);

	// Handles reset timer on new login attempt
	useEffect(() => {
		if (userStore.loginAttemptId && autoStartTimer) {
			setTimer(65);
		}
	}, [userStore.loginAttemptId, autoStartTimer]);

	/**
	 * Cleans phone number by removing non-digit characters
	 * Strips "+1" country code or leading "1" if present
	 * and optionally formats it with dashes
	 *
	 * @param {string} phoneNum - Raw phone number input
	 * @param {boolean} format - Whether to format with dashes (default: false)
	 * @returns {string} - Cleaned phone number (never starts with 1)
	 */
	const cleanPhoneNumber = (phoneNum, format = false) => {
		// Remove all non-digit characters
		let numbers = phoneNum.replace(/\D/g, "");

		// Strip leading "1" if present (US country code)
		// A number should never start with 1
		if (numbers.length === 11 && numbers.startsWith("1")) {
			numbers = numbers.substring(1);
		}

		if (format && numbers.length >= 10) {
			return `${numbers.substring(0, 3)}-${numbers.substring(
				3,
				6
			)}-${numbers.substring(6, 10)}`;
		}
		return numbers;
	};

	/**
	 * Sends verification code to the provided phone number
	 *
	 * Clears any existing code and errors before sending.
	 * On success, sets loginAttemptId and starts the timer.
	 *
	 * @returns {Promise<void>}
	 */
	const handleSendCode = async () => {
		// Prevent duplicate calls
		if (loading) {
			return;
		}

		// Validate phone number
		let phoneToUse =
			cleanedPhone.current.replace(/\D/g, "") || phone.replace(/\D/g, "");

		// Strip leading "1" if present (US country code)
		// A number should never start with 1
		if (phoneToUse.length === 11 && phoneToUse.startsWith("1")) {
			phoneToUse = phoneToUse.substring(1);
		}

		// Validate: must be 10 digits with valid area code (first digit 2-9)
		if (phoneToUse.length !== 10 || !/^[2-9]/.test(phoneToUse)) {
			setError("Enter a valid phone number");
			toast.error("Enter a valid phone number");
			return;
		}

		setLoading(true);
		setError("");
		const sessionid = SESSION_ID();

		try {
			// Send verification code to API (phoneToUse already has leading 1 stripped)
			const response = await userAuthAPI.getphonecode(phoneToUse, sessionid);
			console.log("Create login attempt response:", response);

			// Update state on success
			userStore.setLoginAttemptId(response.result.id);
			setCodeSent(true);

			// Clear the code field when sending a new code
			setCode("");
			// Force remount of code input by changing key
			setCodeInputKey((prev) => prev + 1);

			// Start timer if autoStartTimer is enabled
			if (autoStartTimer) {
				setTimer(65); // Start at 65 for 5 second "code sent" message
			}

			toast.success("Code sent successfully!");
		} catch (error) {
			// Handle error
			setError("No account associated with this phone number.");
			toast.error("No account associated with this phone number.");
			onError(error);
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Verifies the entered code with the server
	 *
	 * On success, calls onSuccess callback.
	 * On failure, displays error and calls onError callback.
	 *
	 * @returns {Promise<void>}
	 */
	const handleVerifyCode = async () => {
		// Prevent duplicate calls
		if (loading) {
			return;
		}

		// Validate code
		if (!code) {
			setError("Please enter the verification code");
			toast.error("Please enter the verification code");
			return;
		}

		setLoading(true);
		setError("");

		try {
			// Verify code with API
			// Use provided creatureID, fallback to user's CreatureID, or null
			const creatureIDToUse =
				creatureID !== null ? creatureID : userStore.user?.CreatureID || null;
			const response = await userAuthAPI.verifyphonecode(
				userStore.loginAttemptId,
				code,
				phone.replace(/\D/g, ""),
				creatureIDToUse
			);

			// Call success handler
			onSuccess(response);
		} catch (error) {
			// Handle error
			setLoading(false);
			setTimer(0); // Reset timer to allow resend
			const errorMessage =
				error.response?.data?.message || "Invalid verification code";
			setError(errorMessage);
			toast.error(errorMessage);
			onError(error);
		}
	};

	/**
	 * Clears the verification code field
	 *
	 * Also increments the codeInputKey to force remount of input
	 */
	const clearCode = () => {
		setCode("");
		setCodeInputKey((prev) => prev + 1);
	};

	/**
	 * Resets all phone verification state
	 */
	const reset = () => {
		setPhone("");
		setCode("");
		setError("");
		setLoading(false);
		setTimer(null);
		setCodeSent(false);
		setCodeInputKey(0);
		cleanedPhone.current = "";
	};

	return {
		// State
		phone,
		code,
		loading,
		error,
		timer,
		codeSent,
		codeInputKey,

		// Setters
		setPhone,
		setCode,
		setError,

		// Handlers
		handleSendCode,
		handleVerifyCode,
		clearCode,
		reset,

		// Utilities
		cleanPhoneNumber,
		cleanedPhone,
	};
};
